import { useState, useEffect, useMemo } from 'react';
import { publicAnalyticsService } from '../api/publicApiClient';
import type { QueryConfig } from '../types/analytics';

interface UsePublicAnalyticsDataProps {
  queryConfig?: QueryConfig | any;
  ownerUserId: string;
  accountId: string;
  shareToken: string;
  scoping?: string;
}

interface UsePublicAnalyticsDataResult {
  data: any;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for fetching widget data via the public (unauthenticated) analytics API.
 * Used by PublicDashboardPage to feed data into WidgetWrapper via externalData prop.
 */
export function usePublicAnalyticsData({
  queryConfig,
  ownerUserId,
  accountId,
  shareToken,
  scoping,
}: UsePublicAnalyticsDataProps): UsePublicAnalyticsDataResult {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const configKey = useMemo(() => JSON.stringify(queryConfig), [queryConfig]);

  useEffect(() => {
    if (!queryConfig || !shareToken) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const parsedConfig = configKey ? JSON.parse(configKey) : {};

        // Strip empty or invalid filters — the API rejects filters with empty field names
        if (Array.isArray(parsedConfig.filters)) {
          parsedConfig.filters = parsedConfig.filters.filter(
            (f: any) => f.field && f.field.trim() !== ''
          );
          if (parsedConfig.filters.length === 0) {
            delete parsedConfig.filters;
          }
        }

        const mergedConfig: QueryConfig = {
          ...parsedConfig,
          ...(scoping && { scoping_type: scoping }),
        };

        const response = await publicAnalyticsService.fetchWidgetData(
          mergedConfig,
          ownerUserId,
          accountId,
          shareToken
        );
        setData(response.data);
      } catch (err: any) {
        console.error('[PublicAnalyticsData] Failed to fetch:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [configKey, ownerUserId, accountId, shareToken, scoping]);

  return { data, loading, error };
}
